import React, { FC, useEffect, useState } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import {
  CustomTooltip,
  renderTooltipFieldValue,
  StackedCell,
  timeLabel,
  timeTooltipLabel,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { DownloadIcon, ListIcon } from 'components/Icons';
import { VulnerabilityTabActions } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Compute/VirtualMachine/Vulnerability/VulnerabilityTabActions';
import { ECRImageContainers } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/ECR/Components/ECRImageContainers';
import { renderVulnerabilities } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { renderImageName } from 'containers/Visibility/Vulnerabilities/Components/hooks/utils';
import { saveAsFile } from 'utils/download';

import { ImageVulnerabilitiesDrawer } from './ImageVulnerabilitiesDrawer';

interface Props {
  vulnerability?: boolean;
}

export const ECRImages: FC<Props> = props => {
  const { vulnerability = true } = props;
  const {
    resourceUuid,
    ecrImages,
    searchTerm: finalSearchTerm,
    resourceAccountId,
    downloadReport,
    resourceInsight,
    resourceDetails,
  } = useSelector(selectActiveResource);
  const { ecrContainerImages } = ecrImages;
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const showContainers = useDisclosure();
  const cvesDisclosure = useDisclosure();

  useEffect(() => {
    setSearchTerm(finalSearchTerm);
  }, [finalSearchTerm]);

  useEffect(() => {
    dispatch(
      actions.getECRContainerImages({
        q: {
          environmentId: resourceInsight.data?.environment_id,
          page: ecrContainerImages.page.info.page_number,
          pageSize: ecrContainerImages.page.info.page_size,
          repositoryUuid: resourceUuid,
          searchTerm: !!finalSearchTerm ? finalSearchTerm : undefined,
        },
      }),
    );
  }, [
    resourceInsight.data,
    dispatch,
    ecrContainerImages.page.info,
    resourceUuid,
    finalSearchTerm,
  ]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
      render: ({ row }) =>
        renderImageName(row, NativeResources.ElasticContainerRegistry),
    },
    {
      header: 'Registry',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row?.registry_id}
          lower={row?.repository_arn ?? row?.repository_name}
        />
      ),
    },
    ...(!vulnerability
      ? [
          {
            header: 'Details',
            align: 'left',
            render: ({ row }) => (
              <StackedCell
                upper={row?.image_size_in_bytes + ' (Bytes)'}
                lower={row?.image_digest}
              />
            ),
          },
        ]
      : []),
    {
      header: 'Status',
      align: 'left',
      render: ({ row }) => {
        return vulnerability ? (
          <CustomTooltip
            label={
              <Stack>
                {renderTooltipFieldValue('Scan status', row?.image_scan_status)}
                {renderTooltipFieldValue(
                  'Last scanned at',
                  timeLabel(row?.last_scanned_at),
                )}
              </Stack>
            }
          >
            <StackedCell
              upper={row?.image_scan_status}
              lower={timeLabel(row?.last_scanned_at)}
              showLowerTooltip={false}
              showUpperTooltip={false}
            />
          </CustomTooltip>
        ) : (
          row?.image_scan_status
        );
      },
    },
    ...(!vulnerability
      ? [
          {
            header: 'Activity',
            align: 'left',
            render: ({ row }) => (
              <StackedCell
                upper={timeLabel(row?.last_recorded_pull_time)}
                upperTooltip={timeTooltipLabel(row?.last_recorded_pull_time)}
                lower={timeLabel(row?.created_at)}
                lowerTooltip={timeTooltipLabel(row?.created_at)}
              />
            ),
          },
        ]
      : []),
    ...(!vulnerability
      ? [
          {
            header: 'Owner',
            align: 'left',
            accessor: 'owner',
          },
        ]
      : [
          {
            header: 'Vulnerabilities',
            align: 'left',
            render: ({ row }) => {
              return renderVulnerabilities({ value: row?.vulnerabilities });
            },
          },
        ]),
    {
      header: 'Actions',
      render: ({ row }) => (
        <Center>
          <ActionButton
            icon={<ListIcon />}
            label="Show containers"
            onClick={e => {
              e.stopPropagation();
              showContainers.onOpen();
              setCurrentRecord(row);
            }}
          />
          <ActionButton
            icon={<DownloadIcon />}
            label="Download vulnerabilities in CSV"
            isLoading={
              downloadReport.isLoading && currentRecord?.id === row?.id
            }
            onClick={e => {
              e.stopPropagation();
              setCurrentRecord(row);
              dispatch(
                actions.downloadReport({
                  q: {
                    accountId: [resourceAccountId],
                    environmentId: resourceInsight.data?.environment_id,
                    imageId: row?.image_digest,
                    resourceType: NativeResources.ElasticContainerRegistry,
                    resourceId: [row?.registry_uuid],
                  },
                  onSuccess: res => {
                    saveAsFile(
                      res,
                      'Ariksa - Vulnerabilities_' +
                        row?.id +
                        '_' +
                        dayjs().format('DD-MMM-YYYY h.mm A') +
                        '.csv',
                      'text/csv;charset=utf-8;',
                    );
                  },
                }),
              );
            }}
          />
        </Center>
      ),
    },
  ];

  return (
    <Box h="full" minH="400px">
      <Stack h="full" spacing={3}>
        <BorderedBox>
          <HStack justify="space-between">
            <Box w="30%">
              <SearchBox
                value={searchTerm}
                onChange={s => {
                  setSearchTerm(s);
                  if (!s) dispatch(actions.updateSearchTerm(''));
                }}
                onSearch={() => dispatch(actions.updateSearchTerm(searchTerm))}
                onClickClose={() => dispatch(actions.updateSearchTerm(''))}
              />
            </Box>
            <VulnerabilityTabActions />
          </HStack>
        </BorderedBox>
        <Box flex={1}>
          <Table
            columns={columns}
            data={ecrContainerImages.data ?? []}
            isLoading={ecrContainerImages.isLoading}
            pagination={{
              pageInfo: ecrContainerImages.page.info,
              onChange: info =>
                dispatch(actions.updateECRImagesPagination(info)),
              totalCount: ecrContainerImages.page.totalCount,
            }}
            onRowClick={row => {
              setCurrentRecord(row);
              vulnerability && cvesDisclosure.onOpen();
            }}
          />
        </Box>
        {vulnerability && cvesDisclosure.isOpen && (
          <ImageVulnerabilitiesDrawer
            isOpen={cvesDisclosure.isOpen}
            onClose={cvesDisclosure.onClose}
            imageDetails={currentRecord}
            resourceInsights={resourceInsight.data}
            resourceDetails={resourceDetails.data}
          />
        )}
        {showContainers.isOpen && (
          <ECRImageContainers
            isOpen={showContainers.isOpen}
            row={currentRecord}
            onClose={showContainers.onClose}
          />
        )}
      </Stack>
    </Box>
  );
};
